<template>
  <div class="flex items-center" :class="containerClass">
    <div class="mr-1.5 employee-status-container" :class="avatarContainerClass">
      <img
        v-if="user.profileImage"
        :src="getEmployeeImage(user)"
        @error="handleImageError"
        :alt="altText || 'User Image'"
        class="avtarSize"
        :style="getImageStyle()"
      />
      <v-avatar
        v-else
        :color="getAvatarColor(getUserFullName(user))"
        :size="size"
        class="avtarSize"
        :style="getAvatarStyle()"
      >
        <span class="white--text font-weight-medium" :class="initialsClass">
          {{ getUserInitials(getUserFullName(user)) }}
        </span>
      </v-avatar>
    </div>
    <div v-if="showName" class="w-full" :class="nameContainerClass">
      <span
        v-if="user"
        class="truncate-name flex items-center font-semibold"
        :class="nameClass"
        v-tooltip="tooltipEnabled ? { text: getUserFullName(user) } : undefined"
      >
        {{ getUserFullName(user) }}
        <span v-if="showEmployeeId" class="ml-1"> / {{ user.employeeId }}</span>
        <slot name="name-content"></slot>
        <span
          v-if="user && user.isOwner"
          class="text-[12px] rounded-xl p-2 ml-2"
          :class="{
            'text-green-600 bg-green-100': !$vuetify.theme.dark,
            'text-white': $vuetify.theme.dark
          }"
          :style="{
            backgroundColor: $vuetify.theme.dark ? $vuetify.theme.currentTheme.secondary : ''
          }"
        >
          OWNER
        </span>
      </span>
      <slot name="additional-content"></slot>
    </div>
  </div>
</template>

<script>
import { fullName } from '@/utils/common'

export default {
  name: 'UserAvatar',
  props: {
    user: {
      type: Object,
      required: true
    },
    size: {
      type: [Number, String],
      default: 32
    },
    showName: {
      type: Boolean,
      default: true
    },
    showEmployeeId: {
      type: Boolean,
      default: false
    },
    tooltipEnabled: {
      type: Boolean,
      default: true
    },
    altText: {
      type: String,
      default: 'User Image'
    },
    containerClass: {
      type: String,
      default: ''
    },
    avatarContainerClass: {
      type: String,
      default: ''
    },
    avatarClass: {
      type: String,
      default: ''
    },
    avatarStyle: {
      type: Object,
      default: () => ({})
    },
    nameContainerClass: {
      type: String,
      default: ''
    },
    nameClass: {
      type: String,
      default: 'text-sm'
    },
    initialsClass: {
      type: String,
      default: 'text-xs'
    }
  },
  data() {
    return {
      baseUrl: process.env.VUE_APP_S3_BASE_URL,
      fallbackImage: require('~/assets/img/genericImage.png')
    }
  },
  methods: {
    getUserInitials(user) {
      if (!user) return ''
      try {
        return user
          .split(' ')
          .filter((word) => word)
          .map((n) => n[0])
          .join('')
          .toUpperCase()
      } catch (error) {
        console.error('Error in getUserInitials:', error)
        return ''
      }
    },
    getAvatarColor(user) {
      const colors = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#607D8B']
      if (!user) return colors[0]
      const index = user.length % colors.length
      return colors[index]
    },
    getEmployeeImage(user) {
      if (user.profileImage) {
        return `${this.baseUrl}${user.profileImage}`
      } else {
        if (user.gender === 'M') {
          return require('~/assets/img/profileMale.png')
        } else if (user.gender === 'F') {
          return require('~/assets/img/profileFemale.png')
        } else {
          return require('~/assets/img/genericImage.png')
        }
      }
    },
    handleImageError(event) {
      event.target.src = this.fallbackImage
    },
    getUserFullName(user) {
      if (user.name) {
        return user.name
      }
      return fullName(user)
    },
    getImageStyle() {
      return {
        ...this.avatarStyle,
        height: `${this.size}px`,
        width: `${this.size}px`,
        minWidth: `${this.size}px`
      }
    },
    getAvatarStyle() {
      return {
        ...this.avatarStyle,
        height: `${this.size}px`,
        width: `${this.size}px`,
        minWidth: `${this.size}px`
      }
    }
  }
}
</script>

<style scoped>
.truncate-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.employee-status-container {
  position: relative;
  display: inline-block;
}

.employee-image,
.employee-icon {
  white-space: nowrap;
  border-radius: 50%;
}

.status-indicator {
  position: absolute;
  bottom: 0;
  right: 0;
}
.avtarSize {
  /* Allow avatarStyle prop to control sizing */
  border-radius: 50%;
}
</style>
